import {
  enableProdMode,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
  provideHttpClient,
  withXhr,
} from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import {
  Route,
  provideRouter,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
  BrowserUtils,
} from '@azure/msal-browser';
import { AppComponent } from './app/app.component';
import { ProfileComponent } from './app/profile/profile.component';
import { environment } from './environments/environment';
import { EnrollPasliteComponent } from './app/enroll-paslite/enroll-paslite.component';
import { ReportOutageComponent } from './app/report-outage/report-outage.component';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

if (environment.production) {
  enableProdMode();
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

declare global {
  interface Window {
    __pasliteBootstrapped?: boolean;
    env?: {
      enableconsent?: boolean;
    };
  }
}

const initialNavigation =
  !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
    ? withEnabledBlockingInitialNavigation() // Set to enabledBlocking to use Angular Universal
    : withDisabledInitialNavigation();

export const Routes: Route[] = [
  {
    path: '',
    redirectTo: 'enroll/texas', // Redirect empty path to home
    pathMatch: 'full',
  },
  {
    path: 'enroll',
    redirectTo: 'enroll/texas', // Redirect empty path to home
    pathMatch: 'full',
  },
  {
    path: 'enroll/:region',
    component: EnrollPasliteComponent,
  },
  {
    path: 'outage',
    redirectTo: 'outage/texas', // Redirect empty path to home
    pathMatch: 'full',
  },
  {
    path: 'outage/:region',
    component: ReportOutageComponent,
  },
];

(window as any).env = {
  enableconsent: environment.enableconsent,
};

// Directly inject the site key on startup
injectSiteKey(environment.recaptcha.siteKey);

function injectSiteKey(siteKey: string): void {
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
  document.body.appendChild(script);
}

async function getEnabledDisclaimer(): Promise<boolean> {
  try {
    const res = await fetch(
      environment.apiUrl + '/api/Common/GetCommonAppConfigVariables',
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = (await res.json()) as Record<string, unknown>;

    const key = Object.keys(data).find(
      (k) => k.toLowerCase() === 'enabledisclaimer',
    );

    const flag = key ? (data as any)[key] : undefined;

    if (flag !== null) {
      return String(flag).toLowerCase() === 'true';
    }
  } catch (err) {
    console.warn(
      'Error fetching ENABLEDISCLAIMER flag from API. Using local env variable:',
      err,
    );
  }
  return environment.ENABLEDISCLAIMER ?? false;
}

//Inject disclaimer script based on API flag
function injectDisclaimerBasedOnAPIFlag(url: string): Promise<void> {
  return new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }

    const existing = document.getElementById('external-disclaimer');
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.id = 'external-disclaimer';
    script.onload = () => {
      //console.log('Disclaimer script loaded');
      resolve();
    };
    script.onerror = (err) => {
      //console.error('Error loading disclaimer script:', err);
      resolve();
    };
    document.body.appendChild(script);
  });
}

// (async () => {

//   if (window.__pasliteBootstrapped) return;
//   window.__pasliteBootstrapped = true;
//   const enableDisclaimer = await getEnabledDisclaimer();

//   if(enableDisclaimer) {
//     await injectDisclaimerBasedOnAPIFlag(environment.disclaimerScriptUrl);
//   }
//   platformBrowserDynamic().bootstrapModule(AppComponent)
//   .catch(err => console.error(err));
// })();

(async () => {
  if ((window as any).__pasliteBootstrapped) {
    return;
  }
  (window as any).__pasliteBootstrapped = true;

  const enableDisclaimer = await getEnabledDisclaimer();

  if (enableDisclaimer) {
    await injectDisclaimerBasedOnAPIFlag(environment.disclaimerScriptUrl);
  }

  await bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
      provideRouter(Routes, initialNavigation),
      provideNoopAnimations(),
      providePrimeNG({ theme: { preset: Aura } }),
    ],
  }).catch((err) => {
    console.error(err);
  });
})();
